import { supabase } from '../lib/supabase'
import type { AdmNfeHeader, AdmNfeItem, AdmNfseHeader, AdmNfseItem } from '../types/admDespesas'

export const admDespesasService = {
  async listNfe(): Promise<AdmNfeHeader[]> {
    const { data, error } = await supabase
      .from('adm_nfe_header')
      .select('*')
      .is('deleted_at', null)
      .order('issue_date', { ascending: false })

    if (error) throw error
    return (data ?? []) as AdmNfeHeader[]
  },

  async listNfse(): Promise<AdmNfseHeader[]> {
    const { data, error } = await supabase
      .from('adm_nfse_header')
      .select('*')
      .is('deleted_at', null)
      .order('issue_date', { ascending: false })

    if (error) throw error
    return (data ?? []) as AdmNfseHeader[]
  },

  async listNfeItems(nfeId: string): Promise<AdmNfeItem[]> {
    const { data, error } = await supabase
      .from('adm_nfe_items')
      .select('*')
      .eq('nfe_id', nfeId)
      .is('deleted_at', null)
      .order('id')

    if (error) throw error
    return (data ?? []) as AdmNfeItem[]
  },

  async listAllNfeItems(): Promise<AdmNfeItem[]> {
    const { data, error } = await supabase
      .from('adm_nfe_items')
      .select('*')
      .is('deleted_at', null)
      .order('nfe_id')

    if (error) throw error
    return (data ?? []) as AdmNfeItem[]
  },

  async listNfseItems(nfseId: string): Promise<AdmNfseItem[]> {
    const { data, error } = await supabase
      .from('adm_nfse_items')
      .select('*')
      .eq('nfse_id', nfseId)
      .is('deleted_at', null)
      .order('id')

    if (error) throw error
    return (data ?? []) as AdmNfseItem[]
  },

  async listAllNfseItems(): Promise<AdmNfseItem[]> {
    const { data, error } = await supabase
      .from('adm_nfse_items')
      .select('*')
      .is('deleted_at', null)
      .order('nfse_id')

    if (error) throw error
    return (data ?? []) as AdmNfseItem[]
  },

  async softDeleteNfe(id: string, reason: string, userId: string): Promise<void> {
    const { error } = await supabase
      .from('adm_nfe_header')
      .update({
        deleted_at: new Date().toISOString(),
        deleted_by: userId,
        deleted_reason: reason,
      })
      .eq('id', id)

    if (error) throw error
  },

  async softDeleteNfse(id: string, reason: string, userId: string): Promise<void> {
    const { error } = await supabase
      .from('adm_nfse_header')
      .update({
        deleted_at: new Date().toISOString(),
        deleted_by: userId,
        deleted_reason: reason,
      })
      .eq('id', id)

    if (error) throw error
  },

  async updateNfeItem(itemId: string, patch: {
    description?: string | null
    ncm?: string | null
    quantity?: number | null
    unit?: string | null
    unit_value?: number | null
    total_value?: number | null
    category_id?: string | null
    cost_center_id?: string | null
  }): Promise<void> {
    const { error } = await supabase
      .from('adm_nfe_items')
      .update(patch)
      .eq('id', itemId)

    if (error) throw error
  },

  async updateNfseItem(itemId: string, patch: {
    description?: string | null
    service_code?: string | null
    quantity?: number | null
    unit_value?: number | null
    total_value?: number | null
    category_id?: string | null
    cost_center_id?: string | null
  }): Promise<void> {
    const { error } = await supabase
      .from('adm_nfse_items')
      .update(patch)
      .eq('id', itemId)

    if (error) throw error
  },

  async softDeleteNfeItem(itemId: string, userId: string): Promise<void> {
    const { error } = await supabase
      .from('adm_nfe_items')
      .update({
        deleted_at: new Date().toISOString(),
        deleted_by: userId,
      })
      .eq('id', itemId)

    if (error) throw error
  },

  async softDeleteNfseItem(itemId: string, userId: string): Promise<void> {
    const { error } = await supabase
      .from('adm_nfse_items')
      .update({
        deleted_at: new Date().toISOString(),
        deleted_by: userId,
      })
      .eq('id', itemId)

    if (error) throw error
  },

  async updateNfeHeader(headerId: string, patch: {
    number?: string | null
    series?: string | null
    issuer_name?: string | null
    issuer_cnpj?: string | null
    recipient_name?: string | null
    recipient_document?: string | null
    access_key?: string
    issue_date?: string | null
    total_products?: number | null
    total_freight?: number | null
    total_discount?: number | null
    total_value?: number | null
    payment_method?: string | null
    file_name?: string | null
  }): Promise<void> {
    const { error } = await supabase
      .from('adm_nfe_header')
      .update(patch)
      .eq('id', headerId)

    if (error) throw error
  },

  async updateNfseHeader(headerId: string, patch: {
    number?: string | null
    rps_number?: string | null
    provider_name?: string | null
    provider_cnpj?: string | null
    provider_im?: string | null
    municipality?: string | null
    customer_name?: string | null
    customer_document?: string | null
    access_key?: string
    issue_date?: string | null
    competence_date?: string | null
    total_services?: number | null
    total_discount?: number | null
    net_value?: number | null
    payment_method?: string | null
    file_name?: string | null
  }): Promise<void> {
    const { error } = await supabase
      .from('adm_nfse_header')
      .update(patch)
      .eq('id', headerId)

    if (error) throw error
  },
}
